<!--
Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
Unauthorized use, copying, distribution, displaying, or public performance
of this file, via any medium, is strictly prohibited. All information
contained herein is proprietary and confidential and may not be shared
with any third party without the express written consent of Reciprocity, Inc.
-->

<template>
  <div class="day-selector-form-presenter">
    <span v-if="data.displayName" class="day-selector-form-presenter__label">
      {{ data.displayName }}
      <span v-if="data.mandatory" class="required">*</span>
    </span>
    <div
      :class="[
        'day-selector-form-presenter__selectable-days',
        { 'day-selector-form-presenter__selectable-days--invalid': validationData.isValid === false },
      ]"
    >
      <day-selector :value="fixedValue" :day-names="dayNames" v-on="inputListeners" />
      <div v-if="validationData.isValid === false" class="day-selector-form-presenter__error-message">
        {{ validationData.errorMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import DaySelector from '@reciprocity/day-selector';
import { eachDayOfInterval, endOfWeek, format, startOfWeek } from 'date-fns';
import BaseFormPresenterMixin from './BaseFormPresenterMixin';

export default {
  name: 'DaySelectorFormPresenter',
  components: {
    DaySelector,
  },
  mixins: [BaseFormPresenterMixin],
  props: {
    value: {
      type: Array,
      default: () => [],
    },
    dayNames: {
      type: Array,
      default: () =>
        eachDayOfInterval({ start: startOfWeek(new Date()), end: endOfWeek(new Date()) }).map(date =>
          format(date, 'EEEE').toLowerCase()
        ),
    },
  },
  computed: {
    predefinedOptions() {
      const validOptions = this.data.validOptions;
      return validOptions && validOptions.length ? validOptions : null;
    },
    inputListeners: function() {
      const vm = this;
      return Object.assign({}, this.$listeners, {
        input: function(event) {
          if (vm.predefinedOptions) {
            const sortedValues = event.sort((a, b) => vm.dayNames.indexOf(a) - vm.dayNames.indexOf(b));
            const mappedValues = sortedValues.map(val => {
              const labelIndex = vm.dayNames.indexOf(val);
              return vm.predefinedOptions[labelIndex];
            });
            vm.$emit('input', mappedValues);
          } else {
            vm.$emit('input', event);
          }
        },
      });
    },
    fixedValue() {
      if (this.predefinedOptions) {
        return this.value.map(val => {
          const optionIndex = this.predefinedOptions.indexOf(val);
          return this.dayNames[optionIndex];
        });
      }
      return this.value;
    },
    validationState() {
      if (!this.validationData.isValid) {
        return this.validationData.isValid;
      }
      return null;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~@/design/colors';

.day-selector-form-presenter {
  display: flex;
  align-items: flex-start;

  &__label {
    padding: 0.5rem 0.5rem 0 0;
  }

  &__selectable-days {
    padding-left: 0.5rem;

    &--invalid::v-deep span.box {
      border-color: $color-amaranth;
      background-color: rgba($color-amaranth, 0.03);
    }
  }

  &__error-message {
    display: block;
    width: 100%;
    margin: 0.25rem;
    font-size: 80%;
    color: $color-amaranth;
  }
}
</style>
